<template>
  <div class='container'>
    <h1 class="title">ADIDA: Automatic Dialect Identification for Arabic</h1>

    <div id='analyzer-input'>
      <div class='notification is-primary'>
        <p>
          This interface is a demo of the <a href="https://camel.abudhabi.nyu.edu/madar/" target="_blank">MADAR project</a>
          dialect identification system developed by <a href="http://www.aclweb.org/anthology/C18-1113" target="_blank">Salameh, Bouamor and Habash (2018)</a>.
        </p>
        <p>
          The system is able to distinguish amongst 25 cities (from Rabat to Muscat) in addition to Modern Standard Arabic.
        </p>
        <p>
          To use the demo enter text in Standard or Dialectal Arabic. The cities and regions that our system identifies will be lit up.
        </p>
        <p>
          The ADIDA web interface is described in the paper by <a href="https://www.aclweb.org/anthology/N19-4002" target="_blank">Obeid, Salameh, Bouamor and Habash (2019)</a>.
        </p>
      </div>
      <form v-on:keyup.enter.prevent="getID">

        <div class="columns">
          <!-- Text Input -->
          <div class="column is-full">
            <label class='label'>Input</label>
            <input dir='rtl' lang='ar' class='input ar-input' type='text' placeholder='Write a word or sentence to identify' v-model='inputText' v-on:keydown.enter.prevent="">
            <p class="has-text-danger" v-if="showInputError">Error: Invalid input. Please make sure you enter Arabic text.</p>
          </div>
        </div>

        <div class="columns">
          <!-- Submit Button -->
          <div class="column is-one-fifth is-flex">
            <a class='button is-primary is-fullwidth align-bottom' v-on:click="getID">Identify</a>
          </div>
        </div>

      </form>
    </div>

    <div id='id-output' v-bind:class="{ closed: !showOutput}">
      <hr>
      <label class='label'>Output</label>
      <div>
        <div class="id-score" v-for="(entry, index) in scoreData" v-if="index < 5"><b>{{ entry.dialect }}:</b> {{ (entry.score * 100).toFixed(1)}}%</div>
        <div class="id-score"><b>Other:</b> {{ (otherScore * 100).toFixed(1)}} %</div>
        <div class="id-score"><b>MSA:</b> {{ (msaScore * 100).toFixed(1)}} %</div>
      </div>
      <div class='box id-wrapper'>
        <!-- OUTPUT -->
        <div id="mapid"></div>
      </div>
    </div>
  </div>
</template>

<script>
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import HeatmapOverlay from 'heatmap.js/plugins/leaflet-heatmap';
import { suggestions } from './Shared/suggestions.json';

// eslint-disable-next-line
const arRE = /^(['ءآأؤإئابةتثجحخدذرزسشصضطظعغـفقكلمنهوىيًٌٍَُِّْٰٱپچڤگ'0-9٠-٩.،؟؛!@#$٪^&*ـ=+()[\]{}<>?,;:`/\\|"]|\s)+$/;
const whitspaceRE = /\s+/g;
const arCharRE = /[^'ءآأؤإئابةتثجحخدذرزسشصضطظعغـفقكلمنهوىيًٌٍَُِّْٰٱپچڤگ'0-9٠-٩.،؟؛!@#$٪^&*ـ=+()[\]{}<>?,;:`/\\|" ]/g;

const mapCenter = {
  lat: 26.0890995,
  lng: 21.71316185,
};

const geolocations = {
  // Uncomment for ADB normilization
  // ABD: { lat: 24.3870789, lng: 54.4185362, name: 'Abu Dhabi' },
  ALE: { lat: 36.2065528, lng: 37.0785453, name: 'Aleppo' },
  ALG: { lat: 36.7538345, lng: 3.0534636, name: 'Algiers' },
  ALX: { lat: 31.2242387, lng: 29.884846, name: 'Alexandria' },
  AMM: { lat: 31.8359753, lng: 35.807547, name: 'Amman' },
  ASW: { lat: 24.0923728, lng: 32.8825966, name: 'Aswan' },
  BAG: { lat: 33.3118099, lng: 44.2858652, name: 'Baghdad' },
  BAS: { lat: 30.5313657, lng: 47.701991, name: 'Basra' },
  BEI: { lat: 33.8892527, lng: 35.4867727, name: 'Beirut' },
  BEN: { lat: 32.0813977, lng: 20.0649607, name: 'Benghazi' },
  CAI: { lat: 30.0595581, lng: 31.2234449, name: 'Cairo' },
  DAM: { lat: 33.5075421, lng: 36.247876, name: 'Damascus' },
  DOH: { lat: 25.2841478, lng: 51.4419567, name: 'Doha' },
  FES: { lat: 34.024029, lng: -5.0367598, name: 'Fez' },
  JED: { lat: 21.4505289, lng: 38.9309624, name: 'Jeddah' },
  JER: { lat: 31.7963272, lng: 35.1108048, name: 'Jerusalem' },
  KHA: { lat: 15.5016995, lng: 32.5025564, name: 'Khartoum' },
  MOS: { lat: 36.353016, lng: 43.07061, name: 'Mosul' },
  MUS: { lat: 23.5841504, lng: 58.3536263, name: 'Muscat' },
  RAB: { lat: 33.9693414, lng: -6.9273026, name: 'Rabat' },
  RIY: { lat: 24.7255553, lng: 46.5423428, name: 'Riyadh' },
  SAL: { lat: 32.0322429, lng: 35.6909932, name: 'Salt' },
  SAN: { lat: 15.3831991, lng: 44.1412405, name: 'Sana\'a' },
  SFX: { lat: 34.7615155, lng: 10.6630587, name: 'Sfax' },
  TRI: { lat: 32.8829717, lng: 13.1708263, name: 'Tripoli' },
  TUN: { lat: 36.7949999, lng: 10.0732378, name: 'Tunis' },
};

const minCircleRadius = 5;
const maxCircleRadius = 50;

const heatMapScale = 100;

export default {
  name: 'Main',
  components: {},
  data() {
    return {
      inputText: suggestions[Math.floor((Math.random() * suggestions.length))],
      cleanedInputText: '',
      output: null,
      showOutput: false,
      map: null,
      basemapLayer: null,
      heatmapLayer: null,
      circlesLayer: null,
      controlLayer: null,
      scoreData: [],
      msaScore: 0.0,
      otherScore: 0.0,
      showInputError: false,
      showLengthError: false,
    };
  },

  methods: {
    validateInput() {
      // return arRE.test(this.inputText);
      this.cleanedInputText = this.inputText.replace(arCharRE, '').replace(whitspaceRE, ' ').trim();
      return this.cleanedInputText.length > 0;
    },

    updateCirclesLayer() {
      const dialects = Object.keys(geolocations);
      const circles = [];
      const scores = this.output.sentences[0].dialectid;

      for (let i = 0; i < dialects.length; i += 1) {
        const dialect = dialects[i];
        const dialectObj = geolocations[dialect];
        const score = (scores[dialect] * 100).toFixed(1);

        if (parseFloat(score) > 0) {
          const radius = ((maxCircleRadius - minCircleRadius) * scores[dialect]) + minCircleRadius;
          const latLng = L.latLng(dialectObj.lat, dialectObj.lng);

          const circle = L.circleMarker(latLng, {
            radius,
            fill: true,
            fillColor: 'blue',
            fillOpacity: 0.5,
          });

          circle.bindPopup(`<b>${dialectObj.name}:</b> ${score}%`);

          circles.push(circle);
        }
      }

      this.circlesLayer = L.layerGroup(circles);
    },

    updateControlLayer() {
      this.controlLayer = L.control.layers({
        'Point Map': this.circlesLayer,
        'Heat Map': this.heatmapLayer,
      }, null);
    },

    getID() {
      this.output = null;
      this.showOutput = false;
      this.showInputError = false;
      this.showLengthError = false;

      if (!this.validateInput()) {
        this.showInputError = true;
        return;
      }

      const splicedInput = this.cleanedInputText.substring(0, 1000);

      this.$http.get(`${API_URL}/getid/?sent=${encodeURIComponent(splicedInput)}`)
        .then((response) => {
          this.showOutput = true;
          this.output = response.body;

          if (this.map === null) {
            this.map = L.map('mapid', {
              minZoom: 3,
              maxZoom: 6,
              center: [mapCenter.lat, mapCenter.lng],
              zoom: 4,
            });
            this.map.scrollWheelZoom.disable();
          }

          this.basemapLayer = L.tileLayer('https://api.mapbox.com/styles/v1/{username}/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}', {
            attribution: 'Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>',
            maxZoom: 18,
            username: 'oo11',
            id: 'ckav6zdze5w511inshyaihqkn',
            accessToken: 'pk.eyJ1Ijoib28xMSIsImEiOiJjams2a3hheW4xdWE4M3hxdWVuM245NDYxIn0.QDW60LMQcW7sllnPWykY7w',
            noWrap: true,
          });

          this.map.addLayer(this.basemapLayer);

          const heatData = {
            max: heatMapScale,
            data: [],
          };

          const dialects = Object.keys(geolocations);

          if (response.body.sentences.length > 0) {
            // Uncomment for ABD normilization
            // const norm = 1.0 / (1.0 + response.body.sentences[0].dialectid.DOH);
            const norm = 1.0;

            this.msaScore = response.body.sentences[0].dialectid.MSA * norm;

            this.scoreData.splice(0, this.scoreData.length);
            for (let i = 0; i < dialects.length; i += 1) {
              const dialect = dialects[i];

              if (dialect === 'MSA') {
                // eslint-disable-next-line
                continue;
              }

              // Uncomment for ABD normilization
              // if (dialect === 'ABD') {
              //   // eslint-disable-next-line
              //   continue;
              // }

              const dialectObj = geolocations[dialect];
              const lat = dialectObj.lat;
              const lng = dialectObj.lng;
              const score = response.body.sentences[0].dialectid[dialect] * norm;
              const count = Math.round(heatMapScale * score);

              heatData.data.push({ lat, lng, count });
              this.scoreData.push({ dialect: dialectObj.name, score });

              // Uncomment for ABD normilization
              // if (dialect === 'DOH') {
              //   const abdDialectObj = geolocations.ABD;
              //   const abdLat = abdDialectObj.lat;
              //   const abdLng = abdDialectObj.lng;
              //   heatData.data.push({ lat: abdLat, lng: abdLng, count });
              //   this.scoreData.push({ dialect: 'Abu Dhabi', score: score * 1.0000001 });
              // }
            }

            this.scoreData.sort((x, y) => y.score - x.score);

            let otherScore = 0.0;

            for (let i = 5; i < this.scoreData.length; i += 1) {
              otherScore += this.scoreData[i].score;
            }

            this.otherScore = otherScore;
          }

          if (this.heatmapLayer === null) {
            const cfg = {
              radius: 80,
              maxOpacity: 0.8,
              scaleRadius: false,
              useLocalExtrema: false,
              latField: 'lat',
              lngField: 'lng',
              valueField: 'count',
            };
            this.heatmapLayer = new HeatmapOverlay(cfg);
          } else {
            this.map.removeLayer(this.heatmapLayer);
          }

          this.heatmapLayer.setData(heatData);

          if (this.circlesLayer !== null) {
            this.map.removeLayer(this.circlesLayer);
          }
          this.updateCirclesLayer();

          if (this.controlLayer !== null) {
            this.map.removeControl(this.controlLayer);
          }
          this.updateControlLayer();
          this.map.addControl(this.controlLayer);

          // Set the default layer
          // this.map.addLayer(this.circlesLayer);
          this.map.addLayer(this.circlesLayer);

          this.map.flyTo([mapCenter.lat, mapCenter.lng], 4);

          $('html, body').animate({
            scrollTop: $('#id-output').offset().top,
          }, 1000);
        },
        (response) => {  // Error callback
          // eslint-disable-next-line
          console.log(response.statusText);
        })
        .catch((error) => {
          // eslint-disable-next-line
          console.log(error);
        });
    },
  },

  watch: {},

  mounted() {

  },
};
</script>

<style lang='scss' scoped>
#mapid {
  height: 100%;
  width: 100%;
}

#id-output {
  margin-top: 1.5em;
}

#id-output.closed {
  height: 0px;
  overflow: hidden;
}

.container {
  padding-left: 1rem;
  padding-right: 1rem;
}

label.checkbox {
  margin-right: 1rem;
}

.id-wrapper {
  width: 100%;
  height: 85vh;
  max-height: 600px;
  padding: 0rem;
  overflow: scroll;
}

.id-score {
  display: inline-block;
  margin-right: 1rem;
}

.ar-input {
  font-size: 1.3em;
}

.align-bottom {
  margin-top: auto;
  margin-bottom: 0;
}

</style>



// WEBPACK FOOTER //
// src/components/Main.vue